// MIDAS V3 — Route layout (Phase 1 + Phase 4 + Phase 3)
//
// Owns the v3-scoped root class (.midas-v3-root + .p-claude or .p-machineid)
// and composes the history rail + chat + artifact dock + pane divider +
// fullscreen portal for the active sub-page.
//
// Phase 1 ships:
//   • Theme class wired to Redux (palette swap persists across refresh)
//   • Ctrl+B / ⌘B keyboard listener for the rail toggle
//   • SCSS token sheet imported here (so it loads once for the whole sub-tree)
//
// Phase 4 adds:
//   • `newChatDialogOpen` local state + handlers wired through to the rail's
//     "+ New chat" buttons (both expanded and collapsed variants).
//   • <NewChatDialogV3 /> mounted at layout level so it sits above the rail
//     and the outlet — and so the dialog state survives sub-route changes.
//
// Phase 3 adds:
//   • Conditional flex layout: when `state.midasPage.artifactTabs.length > 0`,
//     the centre column becomes a flex row of [chat][divider][dock] with a
//     resizable `MidasV3PaneDividerV3` between them. Width is clamped to
//     MIN_CHAT_WIDTH=480 / MIN_DOCK_WIDTH=360 and persisted to
//     `midasV3.artifactDockWidth`.
//   • `<FullscreenArtifactV3 />` portal that covers everything when
//     `midasV3.fullscreenArtifactId` is set.
//   • Single `Esc` key listener with priority: NewChat dialog wins over
//     fullscreen so the two don't fight when both are open.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  setArtifactDockWidth,
  setFullscreenArtifactId,
  toggleRailCollapsed,
} from "../../midasV3Slice";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { Machine } from "../../../machinery/models/models";
import { setMachineContextDirect } from "../../../../shared/chatBot/chatBotSlice";
import chatBotApi from "../../../../shared/chatBot/ChatBotApi";
import { Roles } from "../../../../account/models/AccountModels";
import MidasHistoryRailV3 from "./MidasHistoryRailV3";
import NewChatDialogV3 from "./NewChatDialogV3";
import MidasV3PaneDividerV3, {
  DEFAULT_DOCK_WIDTH,
  MIN_CHAT_WIDTH,
  MIN_DOCK_WIDTH,
} from "./MidasV3PaneDividerV3";
import MidasArtifactPaneV3 from "./artifacts/MidasArtifactPaneV3";
import FullscreenArtifactV3 from "./artifacts/FullscreenArtifactV3";
import { MidasV3HistoryPageContext } from "./history/HistoryPageContext";
// Radix TooltipProvider — hoisted to the v3 layout root (MIDAS-V3-P6 hotfix,
// 2026-05-18). P6 added a `<Tooltip>` to MidasThemeSwitcherV3 (rendered inside
// the history rail brand row), but the previous TooltipProvider lived only
// inside MidasArtifactPaneV3's active-viewer subtree — outside the rail. The
// theme switcher therefore mounted without a provider in scope and Radix
// threw "must be used within TooltipProvider", crashing /midas-v3. Hoisting
// here covers (a) the rail (theme switcher + any future rail tooltips),
// (b) the chat outlet (MessageActionsV3 already assumes a provider in scope),
// (c) the dock (MidasArtifactPaneV3 — its inner provider is now redundant and
// has been removed), and (d) the portals (NewChatDialogV3, FullscreenArtifactV3)
// which are rendered as React children of this layout and so inherit the
// provider context regardless of where their DOM nodes mount. Matches v2's
// MidasV2Layout.tsx:242 pattern exactly (same import path, same delayDuration).
import { TooltipProvider } from "../v2/ui/tooltip";

// Side-effect import — registers the v3 token sheet (scoped to .midas-v3-root).
import "./midas-v3-tokens.scss";
// Side-effect import — registers the <LiveRefV3> styles (.live-ref + animations).
// MIDAS-LIVE-REF (2026-05-28): chip styles + 5 animation keyframes used by
// <LiveRefV3>. Imported here so the rules load once for the whole v3 sub-tree
// rather than at every callsite. The class names are unscoped (.live-ref…)
// but reference CSS custom properties (--card, --border, --accent, --red,
// --green, --muted, --font-mono) that only resolve inside .midas-v3-root,
// so the chip is implicitly scoped by emission site (MIDAS V3 chat only).
import "./live-ref.css";
// Side-effect import — registers the <TelRefV3> styles (.tel-ref). Telemetry
// provenance chip (TEL_REF_RENDERER_SPEC.md). Same scoping rationale as
// live-ref.css above — class names are unscoped but reference v3-root tokens.
import "./tel-ref.css";
// Side-effect import — registers the <CompRefV3> styles (.comp-ref) AND the
// artifact tab-pill blink (.midas-v3-tab-blink) used by the same interaction.
// DIAG-OVERLAY-B (component chips → transient hotspot flash / tab blink).
// Same scoping rationale as live-ref.css / tel-ref.css above.
import "./comp-ref.css";
// Side-effect import — MODEL-PICKER v3.3 atoms: model-picker menu enter,
// attach-column enter/exit/flash keyframes, artifact-choreography collapse
// classes. Reduced-motion safe. Same scoping rationale as above.
import "./midas-v33.css";

// MIDAS-V3-BUGS-0519-R6 sub-task 3 helper — flatten the customerMachinery
// machines map (grouped by some key) into a flat array so we can check if
// machines have been loaded. Mirrors `flattenMachines` in MidasHistoryPageV3
// and MidasHistoryRailV3 (kept in-file to avoid a shared util just for the
// guard — the call sites are read-only).
const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

const MidasV3Layout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useAppSelector((s) => s.midasV3.theme);

  // Phase 4: dialog state lives here so both rail variants (expanded +
  // collapsed) can toggle it and the dialog survives /midas-v3 ↔ history
  // navigation if the user changes their mind mid-flow.
  const [newChatDialogOpen, setNewChatDialogOpen] = useState(false);

  const openNewChatDialog = useCallback(() => {
    setNewChatDialogOpen(true);
  }, []);

  const closeNewChatDialog = useCallback(() => {
    setNewChatDialogOpen(false);
  }, []);

  // Phase 3: artifact dock state.
  const artifactTabsCount = useAppSelector(
    (s) => s.midasPage.artifactTabs.length
  );
  const artifactPanelOpen = useAppSelector((s) => s.midasPage.artifactPanelOpen);
  const persistedDockWidth = useAppSelector(
    (s) => s.midasV3.artifactDockWidth
  );
  const fullscreenArtifactId = useAppSelector(
    (s) => s.midasV3.fullscreenArtifactId
  );

  // ── MIDAS-V3-BUGS-0519-R6 sub-task 3: layout-level machines prefetch ──
  //
  // The history rail's per-row metaText reads `machineNameById` from
  // `customerMachinery.machines` (MidasHistoryRailV3:419-429). On a cold
  // `/midas-v3` load (incognito + F5 from a non-MIDAS page), that slice is
  // empty unless some OTHER page populated it. Result: rail rows render
  // without machine-name pills until the user visits `/midas-v3/history`,
  // which dispatches `getMachines({})` as a side effect.
  //
  // Lift that fetch to layout level so EVERY v3 surface (rail + MachinePill +
  // breadcrumb + NewChatDialog machine picker) gets it on first mount.
  // ONE extra API call on cold mount, zero on warm (the guard skips when
  // machines are already loaded).
  //
  // Mount-only — deps `[]`. The guard prevents re-fetch when the slice is
  // already populated (e.g. user navigated from `/machinery` to `/midas-v3`).
  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector(
    (s) => s.customerMachinery.loading
  );

  // Pilot auto-scope inputs (see the effect below).
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const currentUser = useAppSelector((s) => s.account.user);
  const isMidasPilot = currentUser?.role === Roles.MIDAS_PILOT;
  useEffect(() => {
    if (flattenMachines(machinesMap).length === 0 && !machineryLoading) {
      dispatch(getMachines({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── TAJMAC-PILOT: auto-scope a MidasPilot to the tenant's sole machine ──
  //
  // Confinement gap (review finding): hiding the "Platform" card in NewChatDialogV3
  // stops a pilot CHOOSING platform mode, but not a platform-mode SEND. On first
  // login `chatBot.machineContext` is null, the composer is still enabled
  // (ChatInputAreaV3's `disabled` never considered machine context) and its send
  // dispatches `machineId: machineContext?.machineId` → undefined → `machine_id: null`
  // → an unscoped, fleet-wide chat: precisely the mode this role must never have.
  // Tenant-bounded, so not a data leak, but it defeats the scoping.
  //
  // A pilot tenant holds exactly ONE machine by design (MIDAS_PILOT_ROLE_SPEC.md §1),
  // so closing the gap by auto-scoping is also the better UX — the pilot lands already
  // scoped instead of being asked to choose from a list of one. The machine list is
  // already in the store (the prefetch above guarantees it), so this needs NO new
  // fetch and NO change to the send path.
  //
  // Mirrors NewChatDialogV3.pickMachine's local `setMachineContextDirect` + best-effort
  // bot call, deliberately WITHOUT its startNewChat/setNewThreadPending dispatches —
  // this only sets scope, it must not disturb thread state. Re-applies whenever context
  // is cleared (e.g. New chat), which is the intended behaviour for a role that must
  // never sit in platform mode. Cannot loop: it exits as soon as context is set.
  //
  // Byte-identical for every other role — the first guard exits before touching
  // anything.
  const pilotSyncedMachineRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isMidasPilot || machineContext) return;

    const all = flattenMachines(machinesMap);
    // 0 = not loaded yet (this re-runs when the fetch lands) or an empty tenant;
    // >1 = not the by-design pilot shape, so we do not guess which machine to pick.
    if (all.length !== 1) return;

    const machine = all[0];
    dispatch(
      setMachineContextDirect({
        machineId: machine.id,
        model: machine.model,
        serialNumber: machine.serialNumber,
        internalNumber: machine.internalNumber,
        system: machine.system,
        manufacturerName:
          machine.customBrandName || machine.manufacturer?.name || "",
        typeName: machine.type?.name,
        axisConfigName: machine.axisConfiguration?.name,
        controlSystemBrandName: machine.controlSystemBrand?.name,
        imageContent: machine.image?.content,
        description: machine.description,
        productionYear: machine.productionYear,
      })
    );

    // Tell the bot once per machine — re-scoping locally after a New chat must not
    // re-hit the API (NewChatDialogV3 debounces for the same reason).
    if (pilotSyncedMachineRef.current !== machine.id) {
      pilotSyncedMachineRef.current = machine.id;
      chatBotApi.setMachineContext(machine.id).catch(() => {
        /* Best-effort — bot may be offline; local scope is what gates the send. */
      });
    }
  }, [isMidasPilot, machineContext, machinesMap, dispatch]);

  // Dock visible when the v2 slice has tabs AND the panel is open.
  // (v2's `closeArtifactPanel` keeps tabs in state but flips panelOpen=false,
  // so respect that.)
  const dockVisible = artifactTabsCount > 0 && artifactPanelOpen;

  // Local live width during drag — committed to Redux on dragend (debounced).
  const [liveDockWidth, setLiveDockWidth] = useState<number>(
    persistedDockWidth || DEFAULT_DOCK_WIDTH
  );
  const isDraggingRef = useRef(false);
  const centreColumnRef = useRef<HTMLDivElement | null>(null);

  // ── Phase 5: shared handle to the history page's search input ──
  // The history page assigns its <input> ref into this container on mount
  // (and clears on unmount) via MidasV3HistoryPageContext. The Ctrl+K
  // handler below reads it lazily — so it focuses the live input rather
  // than a stale node. Memoised so the context value identity is stable
  // across renders that don't touch its dependencies (avoids re-mounting
  // the consumer effect that registers the ref).
  const historySearchInputRef = useRef<HTMLInputElement | null>(null);
  // MIDAS-V3-BUGS-0519 sub-task 1: `openNewChatDialog` removed from the context
  // — the history page no longer renders the duplicate `+ Nový chat` button.
  // The rail remains the sole entry point to the New Chat dialog. The shared
  // context now carries only the Ctrl+K search input ref.
  const historyPageContextValue = useMemo(
    () => ({
      searchInputRef: historySearchInputRef,
    }),
    []
  );

  // Keep liveDockWidth in sync with persisted value when not actively dragging.
  useEffect(() => {
    if (!isDraggingRef.current) {
      setLiveDockWidth(persistedDockWidth || DEFAULT_DOCK_WIDTH);
    }
  }, [persistedDockWidth]);

  // Compute clamps based on current centre-column width.
  const clampDockWidth = useCallback((proposed: number): number => {
    const containerWidth = centreColumnRef.current?.clientWidth ?? 0;
    if (containerWidth <= 0) return proposed;
    const maxDock = containerWidth - MIN_CHAT_WIDTH - 8; // 8 = divider hit zone
    const min = MIN_DOCK_WIDTH;
    const max = Math.max(min, maxDock);
    return Math.max(min, Math.min(max, proposed));
  }, []);

  const handleDividerDrag = useCallback(
    (clientX: number) => {
      const el = centreColumnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Dock is on the right — its width is the distance from cursor to
      // the right edge of the centre column.
      const proposed = rect.right - clientX;
      setLiveDockWidth(clampDockWidth(proposed));
    },
    [clampDockWidth]
  );

  const handleDividerDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const handleDividerDragEnd = useCallback(() => {
    isDraggingRef.current = false;
    // Persist the final value (debounced via dragend instead of on every move).
    dispatch(setArtifactDockWidth(clampDockWidth(liveDockWidth)));
  }, [dispatch, liveDockWidth, clampDockWidth]);

  // ─── Consolidated global keyboard layer (Phase 6) ───────────────────
  //
  // ONE useEffect registers a single `keydown` listener for the entire v3
  // surface. Centralising avoids accidental double-handling and gives us a
  // single place to enforce the modal priority.
  //
  // Keys handled:
  //   • Ctrl+B / ⌘B → toggle the history rail. Suppressed when focus is in
  //     a text input / textarea / contentEditable — we don't want to hijack
  //     the browser's "bold selection" / native input bindings.
  //   • Ctrl+K / ⌘K → navigate to `/midas/history`. Suppressed when focus
  //     is in a text input (the user is typing, not commanding).
  //   • Esc → priority:
  //       1. newChatDialogOpen → dialog handles its own Esc (NewChatDialogV3
  //          registers its own listener with priority via document-level
  //          capture; we no-op here so we don't double-close).
  //       2. fullscreenArtifactId → exit fullscreen.
  //       3. otherwise → no-op.
  //
  // Listener is attached unconditionally — we always want to listen for
  // Ctrl+B / Ctrl+K. The Esc branch self-gates via the state checks above.
  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return false;
    };

    const onKey = (e: KeyboardEvent) => {
      const isPrimary = (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey;

      // ─── Ctrl+B / ⌘B — rail toggle ─────────────────────────────────
      if (isPrimary && (e.key === "b" || e.key === "B")) {
        if (isEditableTarget(e.target)) return; // let the browser handle bold
        e.preventDefault();
        dispatch(toggleRailCollapsed());
        return;
      }

      // ─── Ctrl+K / ⌘K — history navigation / search focus ───────────
      //
      // Phase 5 wiring:
      //   • Not on `/midas/history` yet → navigate there. The page will
      //     mount its search input ref into the shared context; the user
      //     can press Ctrl+K again to focus it (or just click).
      //   • Already on `/midas/history` → focus the in-page search
      //     input directly (no new history entry).
      //
      // We deliberately read the ref lazily here rather than holding a
      // closure on a stale value — the history page mounts/unmounts as the
      // user navigates and the ref is the only stable handle.
      if (isPrimary && (e.key === "k" || e.key === "K")) {
        if (isEditableTarget(e.target)) return; // let the browser handle
        e.preventDefault();
        if (location.pathname.startsWith("/midas/history")) {
          const input = historySearchInputRef.current;
          if (input) {
            input.focus();
            // Select existing query so the user can replace it immediately.
            try {
              input.select();
            } catch {
              // Defensive — select() can throw on certain input types in
              // older browsers. Focus alone is sufficient.
            }
          }
        } else {
          navigate("/midas/history");
        }
        return;
      }

      // ─── Esc — modal-stack priority ────────────────────────────────
      if (e.key === "Escape") {
        if (newChatDialogOpen) {
          // NewChatDialogV3 owns its own Esc handler — we yield. This keeps
          // dialog-internal Esc semantics (focus restoration, etc.) intact.
          return;
        }
        if (fullscreenArtifactId) {
          e.preventDefault();
          dispatch(setFullscreenArtifactId(null));
          return;
        }
        // Else: no-op. Other components (e.g. composer suggestions) may
        // bind their own Esc handlers; we don't pre-empt them.
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    dispatch,
    navigate,
    location.pathname,
    newChatDialogOpen,
    fullscreenArtifactId,
  ]);

  // Final dock width used for layout (clamped against current container).
  const dockWidth = clampDockWidth(liveDockWidth);
  // Chat column width for the divider chip readout — purely visual.
  const containerWidth = centreColumnRef.current?.clientWidth ?? 0;
  const chatWidthForChip = Math.max(
    MIN_CHAT_WIDTH,
    containerWidth - dockWidth - 8
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={`midas-v3-root p-${theme}`}
        style={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          minHeight: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          // Background is provided by .midas-v3-root via the token sheet.
        }}
      >
        <MidasHistoryRailV3 onNewChat={openNewChatDialog} />
        <div
          ref={centreColumnRef}
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "row",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Chat column */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Phase 5: provide shared context for the history page so it can
                (a) open NewChatDialogV3 (state lives here), and (b) hand its
                search input ref back up to the global Ctrl+K listener. */}
            <MidasV3HistoryPageContext.Provider value={historyPageContextValue}>
              <Outlet />
            </MidasV3HistoryPageContext.Provider>
          </div>

          {/* Resizable divider + dock — only when an artifact is open. */}
          {dockVisible && (
            <>
              <MidasV3PaneDividerV3
                onDrag={handleDividerDrag}
                onDragStart={handleDividerDragStart}
                onDragEnd={handleDividerDragEnd}
                chatWidth={chatWidthForChip}
                dockWidth={dockWidth}
              />
              <div
                style={{
                  width: dockWidth,
                  flexShrink: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <MidasArtifactPaneV3 />
              </div>
            </>
          )}
        </div>
        <NewChatDialogV3 open={newChatDialogOpen} onClose={closeNewChatDialog} />
        {/* Fullscreen overlay — self-portal to document.body; renders nothing
            when fullscreenArtifactId is null. */}
        <FullscreenArtifactV3 />
      </div>
    </TooltipProvider>
  );
};

export default MidasV3Layout;
